/**
 * Hedge & Risk — real Polymarket hedging analytics (no dummies).
 * Reads GET /api/v1/admin/hedges (the hedge_requests table). Shows: what went out to Polymarket
 * (token = which PM market, notional, when), the real fill (avg price), status, per-market hedge +
 * liability, and the liability snapshots (peak). Click a market to drill into its specific hedges.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { apiService } from '../services/apiService';
import './HedgeRiskPage.css';

interface HOrder {
  id: string; at: string; hedgedAt: string; marketId: string; market: string; outcome: string | null;
  pmTokenId: string | null; kind: string; notionalUsd: number; status: string; orderId: string | null;
  limitPrice: number | null; filledShares: number | null; spentUsd: number | null; avgPrice: number | null;
  requestedLiabilityUsd: number | null; attempts: number; error: string | null;
}
interface HMarket { marketId: string; headline: string; pmTokenId: string | null; hedges: number; notionalUsd: number; hedgedUsd: number; liabilityUsd: number; }
interface HData {
  summary: { total: number; pending: number; claimed: number; filled: number; failed: number; skipped: number;
    notionalUsd: number; hedgedUsd: number; filledShares: number; avgFillPrice: number | null; fillRate: number; currentLiabilityUsd: number; };
  byMarket: HMarket[];
  orders: HOrder[];
  liabilityTimeline: { at: string; liabilityUsd: number }[];
  liabilityPeak: { at: string; liabilityUsd: number } | null;
}

const usd = (v: number | null | undefined) =>
  v == null ? '—' : '$' + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const price = (v: number | null) => (v == null ? '—' : '$' + v.toFixed(3));
const shortTok = (t: string | null) => (t ? t.slice(0, 8) + '…' + t.slice(-4) : '—');
const pct = (v: number) => Math.round(v * 100) + '%';

const HedgeRiskPage: React.FC = () => {
  const [data, setData] = useState<HData | null>(null);
  const [marketId, setMarketId] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [error, setError] = useState<string | null>(null);
  const [live, setLive] = useState(false);
  const timer = useRef<ReturnType<typeof setInterval> | null>(null);

  const load = useCallback(async () => {
    try {
      const d = await apiService.getAdminHedges(marketId || undefined);
      setData(d); setLive(true); setError(null);
    } catch (e: any) { setLive(false); setError(e?.message || 'Failed to load hedge data'); }
  }, [marketId]);

  useEffect(() => {
    load();
    timer.current = setInterval(load, 3000);
    return () => { if (timer.current) clearInterval(timer.current); };
  }, [load]);

  const s = data?.summary;
  const orders = (data?.orders || []).filter((o) => statusFilter === 'all' || o.status === statusFilter);
  const selectedMarket = marketId ? data?.byMarket.find((m) => m.marketId === marketId) : null;
  const maxLiab = Math.max(1, ...(data?.liabilityTimeline || []).map((p) => p.liabilityUsd));

  return (
    <div className="hedge-risk-page">
      <div className="page-head">
        <div>
          <h1 className="page-title">Hedge &amp; Risk</h1>
          <p className="page-sub">
            {selectedMarket
              ? <>Markt: <strong>{selectedMarket.headline}</strong> · <button className="hr-link" onClick={() => setMarketId(null)}>← alle Märkte</button></>
              : 'Echtes Polymarket-Hedging — Orders, Fill-Preise & Liability (aus hedge_requests)'}
          </p>
        </div>
        <div className={`hr-live ${live ? 'on' : 'off'}`}><span className="hr-dot" />{live ? 'LIVE' : 'OFFLINE'}</div>
      </div>

      {error && <div className="hr-error">{error}</div>}

      <div className="hr-stats">
        <div className="hr-card"><div className="hr-l">Gehedged (gefüllt)</div><div className="hr-v">{usd(s?.hedgedUsd)}</div><div className="hr-f">{s ? `${s.filled} Orders gefüllt` : ''}</div></div>
        <div className="hr-card"><div className="hr-l">Notional rausgeschickt</div><div className="hr-v">{usd(s?.notionalUsd)}</div><div className="hr-f">{s ? `${s.total} Hedge-Orders` : ''}</div></div>
        <div className="hr-card"><div className="hr-l">Fill-Rate</div><div className="hr-v">{s ? pct(s.fillRate) : '—'}</div><div className="hr-f">{s ? `${s.pending} pending · ${s.failed} failed` : ''}</div></div>
        <div className="hr-card"><div className="hr-l">Liability jetzt</div><div className="hr-v">{usd(s?.currentLiabilityUsd)}</div><div className="hr-f">offenes Risiko (live)</div></div>
        <div className="hr-card"><div className="hr-l">Liability-Peak</div><div className="hr-v">{usd(data?.liabilityPeak?.liabilityUsd)}</div><div className="hr-f">{data?.liabilityPeak ? new Date(data.liabilityPeak.at).toLocaleString() : 'noch kein Hedge-Snapshot'}</div></div>
      </div>

      <div className="hr-grid">
        <div className="hr-feed">
          <div className="hr-toolbar">
            <span className="hr-toolbar-title">Polymarket-Order-Log{selectedMarket ? ' (gefiltert)' : ''}</span>
            <label className="hr-filter">Status
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
                <option value="all">alle</option><option value="filled">filled</option><option value="pending">pending</option>
                <option value="claimed">claimed</option><option value="failed">failed</option><option value="skipped">skipped</option>
              </select>
            </label>
            <span className="hr-count">{orders.length} Orders</span>
          </div>
          <div className="hr-table-wrap">
            <table className="hr-table">
              <thead><tr>
                <th>Zeit</th><th>Markt</th><th>Outcome</th><th>PM-Token</th><th>Art</th>
                <th className="num">Notional</th><th>Status</th><th className="num">Ø-Preis</th><th className="num">Liab@</th>
              </tr></thead>
              <tbody>
                {orders.length === 0 && <tr><td colSpan={9} className="hr-empty">Noch keine Hedge-Orders{selectedMarket ? ' für diesen Markt' : ''}.</td></tr>}
                {orders.map((o) => (
                  <tr key={o.id} title={o.error || o.orderId || ''}>
                    <td className="hr-dim">{new Date(o.at).toLocaleTimeString()}</td>
                    <td className="hr-market" title={o.market}>{o.market}</td>
                    <td>{o.outcome || '—'}</td>
                    <td className="hr-dim hr-mono" title={o.pmTokenId || ''}>{shortTok(o.pmTokenId)}</td>
                    <td><span className="hr-kind">{o.kind}</span></td>
                    <td className="num hr-strong">{usd(o.notionalUsd)}</td>
                    <td><span className={`hr-status hr-status-${o.status}`}>{o.status}</span></td>
                    <td className="num">{price(o.avgPrice)}</td>
                    <td className="num hr-dim">{usd(o.requestedLiabilityUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="hr-side">
          <div className="hr-side-title">Märkte nach Hedge-Volumen <span className="hr-dim">(klick = Drill-down)</span></div>
          {data?.byMarket?.length ? data.byMarket.map((m) => (
            <div className={`hr-mkt ${marketId === m.marketId ? 'active' : ''}`} key={m.marketId} onClick={() => setMarketId(m.marketId === marketId ? null : m.marketId)}>
              <div className="hr-mkt-name" title={m.headline}>{m.headline}</div>
              <div className="hr-mkt-row">
                <span>{m.hedges}× · {usd(m.notionalUsd)}</span>
                <span className="hr-mkt-liab">Liab {usd(m.liabilityUsd)}</span>
              </div>
            </div>
          )) : <div className="hr-empty">Noch keine Hedges.</div>}

          <div className="hr-side-title" style={{ marginTop: '18px' }}>Liability-Verlauf <span className="hr-dim">(Hedge-Snapshots)</span></div>
          {data?.liabilityTimeline?.length ? (
            <div className="hr-spark">
              {data.liabilityTimeline.slice(-40).map((p, i) => (
                <div key={i} className="hr-bar" style={{ height: Math.max(3, Math.round((p.liabilityUsd / maxLiab) * 60)) + 'px' }} title={`${usd(p.liabilityUsd)} @ ${new Date(p.at).toLocaleString()}`} />
              ))}
            </div>
          ) : <div className="hr-empty">Noch keine Liability-Snapshots (entstehen bei Aggregat-Hedges).</div>}
        </div>
      </div>
    </div>
  );
};

export default HedgeRiskPage;
